import { LocationAutocomplete } from './actionTypes';
import { doRequest, LocationUtils, StringUtils } from '../../utils';
import { LocationProductType, LocationHotelsSuppliers } from '../../models';

const requestingLocationAutocomplete = (token, departureOrArrival, locationProductType) => ({
    type: LocationAutocomplete.REQUEST_LOCATION_AUTOCOMPLETE,
    token,
    departureOrArrival,
    locationProductType,
});

const receiveLocationAutocomplete = ({
    token,
    locationAutocomplete,
    departureOrArrival,
    locationProductType,
}) => ({
    type: LocationAutocomplete.RECEIVE_LOCATION_AUTOCOMPLETE,
    token,
    locationAutocomplete,
    departureOrArrival,
    locationProductType,
});

const failLocationAutocomplete = (token, departureOrArrival, locationProductType) => ({
    type: LocationAutocomplete.FAIL_LOCATION_AUTOCOMPLETE,
    token,
    departureOrArrival,
    locationProductType,
});

const getAutocompleteUrl = (token, departureOrArrival, locationProductType) => {
    // autocomplete API product param only accepts FLIGHTS and HOTEL values.
    // We need to get the correct value when locationProductType is Dynpack
    const productType =
        locationProductType === LocationProductType.DP
            ? LocationProductType.HOTEL
            : locationProductType;
    const nearestLocations =
        locationProductType === LocationProductType.FLIGHT || locationProductType === LocationProductType.TRAIN
            ? ';nearestLocations=true'
            : '';
    const addSearchByRegion =
        locationProductType === LocationProductType.FLIGHT || locationProductType === LocationProductType.TRAIN
            ? ';addSearchByRegion=true'
            : '';
    const locale =
        locationProductType === LocationProductType.DP || locationProductType === LocationProductType.FLIGHT
            ? window.reactContext.lang?.replace('-', '_')
            : '';
    const localeParam = locale ? `;locale=${locale}` : '';
    return (
        '/frontend-home/service/geo/itinerary/autocomplete' +
        `;searchWord=${token}` +
        `;departureOrArrival=${departureOrArrival}` +
        ';addSearchByCountry=true' +
        addSearchByRegion +
        nearestLocations +
        localeParam +
        `;product=${productType}`
    );
};

const getHotelAutocompleteUrl = (token, supplier) => {
    if (supplier === LocationHotelsSuppliers.HSA) {
        return getAutocompleteUrl(token, 'ARRIVAL', LocationProductType.HOTEL);
    }

    return `/frontend-home/service/geo/autocompleteSearch;searchParam=${token};supplier=${supplier}`;
};

const getHotelAccommodationAutocompleteUrl = (token, supplier) => {
    if (supplier === LocationHotelsSuppliers.HSA) {
        return `/frontend-home/service/accommodation-autocomplete/search?searchTerm=${encodeURIComponent(
            token,
        )}`;
    }
    return '';
};

const getHotelAutocompleteUrlSearchByRegion = ({ token, sizeRegion, sizeAccommodation }) => {
    const locale = window.reactContext.lang?.replace('-', '_');
    return `/frontend-home/service/accommodation-autocomplete/v3/search?searchTerm=${encodeURIComponent(
        token,
    )}${locale ? `&locale=${locale}` : ''}${sizeRegion ? `&sizeRegion=${sizeRegion}` : ''}${
        sizeAccommodation ? `&sizeAccommodation=${sizeAccommodation}` : ''
    }`;
};

const fetchLocationAutocomplete = (token, departureOrArrival, locationProductType) => {
    const url = getAutocompleteUrl(token, departureOrArrival, locationProductType);
    return (dispatch) => {
        dispatch(requestingLocationAutocomplete(token, departureOrArrival, locationProductType));
        return doRequest(url)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Location autocomplete request failed with status ${response.status}`);
                }
                return response.json();
            })
            .then((locationAutocomplete) => {
                dispatch(
                    receiveLocationAutocomplete({
                        token,
                        locationAutocomplete,
                        departureOrArrival,
                        locationProductType,
                    }),
                );
            })
            .catch((error) => {
                if (
                    locationProductType !== LocationProductType.FLIGHT &&
                    locationProductType !== LocationProductType.TRAIN
                ) {
                    throw error;
                }
                dispatch(failLocationAutocomplete(token, departureOrArrival, locationProductType));
            });
    };
};

const shouldFetchLocationAutocomplete = ({
    activeProduct,
    locationAutocomplete,
    token,
    autocompleteOptions,
    departureOrArrival,
    locationProductType,
}) => {
    const cachedLocation =
        locationAutocomplete[locationProductType][departureOrArrival].locations[token];
    return (
        (!cachedLocation || cachedLocation.requestStatus === 'error') &&
        LocationUtils.isKeywordLongEnoughAccordingWithLanguage(
            token,
            autocompleteOptions,
            activeProduct,
            departureOrArrival,
        )
    );
};

export const getLocationAutocomplete = ({
    activeProduct,
    displayedWord,
    autocompleteOptions,
    departureOrArrival,
    locationProductType,
    useFullSearch = false,
}) => {
    const token = useFullSearch
        ? LocationUtils.getFullLocationsKey(displayedWord)
        : LocationUtils.getLocationsKey(displayedWord, autocompleteOptions);

    return (dispatch, getState) => {
        const { locationAutocomplete } = getState();
        if (
            shouldFetchLocationAutocomplete({
                activeProduct,
                locationAutocomplete,
                token,
                autocompleteOptions,
                departureOrArrival,
                locationProductType,
            })
        ) {
            return dispatch(
                fetchLocationAutocomplete(token, departureOrArrival, locationProductType),
            );
        } else {
            return Promise.resolve();
        }
    };
};

const requestingHotelLocationAutocomplete = (token, subscription) => ({
    type: LocationAutocomplete.REQUEST_HOTEL_LOCATION_AUTOCOMPLETE,
    token,
    subscription,
});

const receiveHotelLocationAutocomplete = ({
    token,
    subscription,
    locationAutocomplete,
    locationAccommodationAutocomplete,
    supplier,
    executionTime,
}) => ({
    type: LocationAutocomplete.RECEIVE_HOTEL_LOCATION_AUTOCOMPLETE,
    token,
    subscription,
    locationAutocomplete,
    locationAccommodationAutocomplete,
    supplier,
    executionTime,
});

const fetchHotelLocationAutocomplete = (token, subscription, supplier) => {
    const urlHotelAutocompleteUrl = getHotelAutocompleteUrl(token, supplier);
    const urlHotelAccommodationAutocomplete = getHotelAccommodationAutocompleteUrl(token, supplier);

    const requests = [doRequest(urlHotelAutocompleteUrl)];
    if (urlHotelAccommodationAutocomplete) {
        requests.push(doRequest(urlHotelAccommodationAutocomplete));
    }
    return (dispatch) => {
        const startTime = Date.now();
        dispatch(requestingHotelLocationAutocomplete(token, subscription));
        return Promise.all(requests)
            .then((response) => {
                if (response[0].ok) {
                    return Promise.all(response.map((r) => r.json()));
                } else {
                    throw new Error(
                        `hotelsPrimeLocationAPI: ${response.statusText} for token: ${token}`,
                    );
                }
            })
            .then((responses) => {
                const endTime = new Date().getTime();
                const executionTime = (endTime - startTime) / 1000;
                const locationAutocomplete = responses[0];
                const locationAccommodationAutocomplete = urlHotelAccommodationAutocomplete
                    ? responses[1]
                    : [];
                dispatch(
                    receiveHotelLocationAutocomplete({
                        token,
                        subscription,
                        locationAutocomplete,
                        locationAccommodationAutocomplete,
                        supplier,
                        executionTime,
                    }),
                );
            })
            .catch((e) => {
                const endTime = new Date().getTime();
                const executionTime = (endTime - startTime) / 1000;
                console.error(e);
                const locationAutocomplete =
                    supplier === LocationHotelsSuppliers.HSA ? [] : { suggestions: [] };
                const locationAccommodationAutocomplete = [];
                dispatch(
                    receiveHotelLocationAutocomplete({
                        token,
                        subscription,
                        locationAutocomplete,
                        locationAccommodationAutocomplete,
                        supplier,
                        executionTime,
                    }),
                );
            });
    };
};

export const getHotelLocationAutocomplete = ({
    activeProduct,
    displayedWord,
    autocompleteOptions,
    subscription,
    supplier = LocationHotelsSuppliers.HSA,
}) => {
    const token = StringUtils.replaceAccents(displayedWord.toLowerCase());
    return (dispatch, getState) => {
        const { locationAutocomplete } = getState();

        if (
            !locationAutocomplete[LocationProductType.HOTEL][subscription].locations[token] &&
            LocationUtils.isKeywordLongEnoughAccordingWithLanguage(
                token,
                autocompleteOptions,
                activeProduct,
            )
        ) {
            return supplier === LocationHotelsSuppliers.HSA
                ? dispatch(fetchHotelLocationAutocompleteByRegion({
                    token,
                    subscription,
                    supplier,
                    sizeRegion: LocationUtils.getAutocompleteMaxSuggestions(activeProduct, autocompleteOptions),
                    sizeAccommodation: LocationUtils.getAutocompleteMaxAccommodationsSuggestions(activeProduct, autocompleteOptions),
                }))
                : dispatch(fetchHotelLocationAutocomplete(token, subscription, supplier));
        } else {
            return Promise.resolve();
        }
    };
};

// ─── DP geoRegion autocomplete (new endpoint, AB-gated) ──────────────────────

const getDpGeoRegionAutocompleteUrl = (token) => getHotelAutocompleteUrlSearchByRegion({ token });

const requestingDpLocationAutocompleteGeoRegion = (token) => ({
    type: LocationAutocomplete.REQUEST_DP_LOCATION_AUTOCOMPLETE_GEOREGION,
    token,
});

const receiveDpLocationAutocompleteGeoRegion = ({ token, locationAutocomplete }) => ({
    type: LocationAutocomplete.RECEIVE_DP_LOCATION_AUTOCOMPLETE_GEOREGION,
    token,
    locationAutocomplete,
});

const fetchDpLocationAutocompleteGeoRegion = (token) => {
    const url = getDpGeoRegionAutocompleteUrl(token);
    return (dispatch) => {
        dispatch(requestingDpLocationAutocompleteGeoRegion(token));
        return doRequest(url)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`DP geoRegion autocomplete error: ${response.status}`);
                }
                return response.json();
            })
            .then((locationAutocomplete) => {
                dispatch(receiveDpLocationAutocompleteGeoRegion({ token, locationAutocomplete }));
            })
            .catch(() => {
                // Fallback to v1 on any failure (SE-3807 TC-3)
                dispatch(fetchLocationAutocomplete(token, 'ARRIVAL', LocationProductType.DP));
            });
    };
};

export const getDpLocationAutocompleteGeoRegion = ({
    activeProduct,
    displayedWord,
    autocompleteOptions,
}) => {
    // Keyed by the whole word, like hotels, so every letter typed is a new query
    // instead of reusing the result of the first few characters.
    const token = StringUtils.replaceAccents(displayedWord.toLowerCase());

    return (dispatch, getState) => {
        const { locationAutocomplete } = getState();
        if (
            !locationAutocomplete[LocationProductType.DP]['ARRIVAL'].locations[token] &&
            LocationUtils.isKeywordLongEnoughAccordingWithLanguage(
                token,
                autocompleteOptions,
                activeProduct,
            )
        ) {
            return dispatch(fetchDpLocationAutocompleteGeoRegion(token));
        }
        return Promise.resolve();
    };
};

const requestingLocationAutocompleteByRegion = (token, subscription) => ({
    type: LocationAutocomplete.REQUEST_HOTEL_LOCATION_AUTOCOMPLETE_V2,
    token,
    subscription,
});

const receiveHotelLocationAutocompleteByRegion = ({
    token,
    subscription,
    locationAutocompleteByRegion,
    supplier,
    executionTime,
}) => ({
    type: LocationAutocomplete.RECEIVE_HOTEL_LOCATION_AUTOCOMPLETE_V2,
    token,
    subscription,
    locationAutocompleteByRegion,
    supplier,
    executionTime,
});

const fetchHotelLocationAutocompleteByRegion = ({
    token,
    subscription,
    supplier,
    sizeRegion,
    sizeAccommodation,
}) => {
    const urlHotelAccommodationAutocomplete = getHotelAutocompleteUrlSearchByRegion({
        token,
        sizeAccommodation,
        sizeRegion,
    });

    return (dispatch) => {
        const startTime = Date.now();
        dispatch(requestingLocationAutocompleteByRegion(token, subscription));
        return doRequest(urlHotelAccommodationAutocomplete)
            .then((response) => {
                if (response.ok) {
                    return response.json();
                } else {
                    throw new Error(
                        `hotelsPrimeLocationAPI: ${response.statusText} for token: ${token}`,
                    );
                }
            })
            .then((locationAutocompleteByRegion) => {
                const endTime = new Date().getTime();
                const executionTime = (endTime - startTime) / 1000;
                dispatch(
                    receiveHotelLocationAutocompleteByRegion({
                        token,
                        subscription,
                        locationAutocompleteByRegion,
                        supplier,
                        executionTime,
                    }),
                );
            })
            .catch((e) => {
                const endTime = new Date().getTime();
                const executionTime = (endTime - startTime) / 1000;
                console.error(e);
                dispatch(
                    receiveHotelLocationAutocompleteByRegion({
                        token,
                        subscription,
                        locationAutocompleteByRegion: {
                            suggestions: [],
                        },
                        supplier,
                        executionTime,
                    }),
                );
            });
    };
};
